import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Factory,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  ClipboardList,
  Star,
  Target,
  BarChart3,
  Building2,
  HeartPulse,
} from "lucide-react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const USFDA_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-red-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-rose-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <HeartPulse size={18} className="text-red-400" />
            <span>U.S. Regulatory Compliance</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-red-400 via-rose-400 to-pink-400">
            USFDA Certification
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            U.S. Food and Drug Administration - Ensuring Safety, Quality, and
            Efficacy for the American Market
          </p>
        </motion.div>
      </section>

      {/* What is USFDA */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is USFDA Certification?
          </h2>
          <div className="space-y-6 text-lg text-gray-700">
            <p>
              USFDA certification refers to the process through which the Food
              and Drug Administration ensures that a product or facility
              complies with U.S. laws and regulations. This includes ensuring
              that products meet safety, quality, and efficacy standards.
            </p>
            <p>
              The U.S. Food and Drug Administration (USFDA) is a federal agency
              of the United States Department of Health and Human Services
              responsible for regulating food safety, pharmaceuticals, medical
              devices, and cosmetics. USFDA certification is a critical
              requirement for organizations that wish to market their products
              in the U.S.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Key Features of USFDA Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Regulatory Compliance",
                desc: "Ensures products meet FDA standards for safety, quality, and efficacy.",
                icon: ShieldCheck,
              },
              {
                title: "Food Safety",
                desc: "Establishes food products are safe for consumption and meet FDA requirements.",
                icon: CheckCircle,
              },
              {
                title: "Drug and Medical Device Approval",
                desc: "Ensures pharmaceuticals and medical devices are safe and effective.",
                icon: HeartPulse,
              },
              {
                title: "Labeling & Packaging Standards",
                desc: "Ensures proper labeling with ingredients, warnings, and usage instructions.",
                icon: FileCheck,
              },
              {
                title: "Market Access",
                desc: "Opens doors to the U.S., one of the world's largest regulated markets.",
                icon: Globe,
              },
              {
                title: "GMP Compliance",
                desc: "Adheres to Good Manufacturing Practices for product quality.",
                icon: Factory,
              },
            ].map((feature, i) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                >
                  <IconComponent className="w-10 h-10 text-red-500 mb-4" />
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-700">{feature.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* FDA Compliance */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is FDA Compliance?
          </h2>
          <div className="bg-gradient-to-r from-red-50 to-rose-50 p-8 rounded-lg">
            <p className="text-lg text-gray-700 mb-6">
              FDA compliance involves ensuring that a product or organization
              adheres to the regulations established by the U.S. Food and Drug
              Administration:
            </p>
            <ul className="space-y-3">
              {[
                "Facility Inspections - Ensuring manufacturing facilities meet FDA standards",
                "Product Approval - Gaining pre-market approval for drugs, devices, and food additives",
                "Labeling Requirements - Ensuring correct labeling with required information",
                "GMP (Good Manufacturing Practices) - Adhering to guidelines for production",
                "FDA compliance is a legal requirement for all businesses marketing products in the U.S.",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </section>

      {/* Importance */}
      <section className="py-16 px-4 bg-gray-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Importance of USFDA Certification for Businesses
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Ensures public safety with FDA's stringent safety standards",
              "Ensures legal compliance necessary to market products in the U.S.",
              "Opens doors to the U.S., largest regulated consumer market",
              "Demonstrates commitment to safety, quality, and regulatory adherence",
              "Recognized worldwide as high standard of safety and quality",
              "Enables international market credibility and expansion",
            ].map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-4"
              >
                <Star className="w-6 h-6 text-rose-500 mt-1 flex-shrink-0" />
                <span className="text-gray-700 text-lg">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Significance */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Significance of USFDA Certification
          </h2>
          <p className="text-lg text-gray-700 mb-6">
            USFDA certification helps organizations:
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Gain Market Access to the U.S. market for product sales",
              "Ensure Product Safety for consumer use and protection",
              "Maintain Regulatory Compliance avoiding legal liabilities",
              "Build Consumer Trust through FDA-approved products",
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
                className="bg-gradient-to-br from-red-100 to-rose-100 p-6 rounded-lg"
              >
                <CheckCircle className="w-6 h-6 text-red-600 mb-3" />
                <p className="text-gray-800 font-medium">{item}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Advantages */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Advantages of USFDA Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Regulatory Compliance adhering to U.S. government requirements",
              "Market Access required to legally sell in the U.S.",
              "Increased Brand Trust from FDA-approved products",
              "Product Quality and Safety ensuring consumer trust",
              "Global Credibility enhancing international reputation",
              "Reduced Liability minimizing legal and safety risks",
              "Consumer Confidence supporting higher sales",
              "International Recognition for non-U.S. markets",
            ].map((advantage, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-3 p-4 bg-white rounded-lg"
              >
                <CheckCircle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{advantage}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Industries */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Industries That Require USFDA Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Food and Beverage",
              "Pharmaceuticals",
              "Medical Devices and Equipment",
              "Cosmetics and Personal Care",
              "Biotechnology",
              "Healthcare and Wellness Products",
            ].map((industry, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 p-4 bg-gradient-to-r from-red-50 to-rose-50 rounded-lg"
              >
                <Factory className="w-5 h-5 text-red-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{industry}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* How to Get Certification */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            How to Get USFDA Certification
          </h2>
          <div className="space-y-6">
            {[
              {
                step: 1,
                title: "Pre-Market Approval",
                desc: "Submit new products to FDA for approval before marketing",
              },
              {
                step: 2,
                title: "Facility Registration",
                desc: "Register manufacturing facilities with FDA for inspections",
              },
              {
                step: 3,
                title: "FDA Inspections",
                desc: "Undergo FDA inspections to verify safety and operational compliance",
              },
              {
                step: 4,
                title: "Documentation",
                desc: "Maintain comprehensive records of production, testing, and safety",
              },
              {
                step: 5,
                title: "Ongoing Compliance",
                desc: "Comply with FDA regulations and submit required reports",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                className="flex gap-6 items-start"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-red-500 to-rose-500 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                  {item.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-r from-red-50 via-rose-50 to-pink-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900 text-center">
            Why Choose MBA Solutions for USFDA Certification?
          </h2>
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <p className="text-lg text-gray-700 mb-6">
              MBA Solutions Pvt. Ltd. provides expert USFDA consultancy
              services, helping businesses navigate the FDA certification
              process for food, drugs, medical devices, and cosmetics.
            </p>
            <ul className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "Pre-Market Submission support and guidance",
                "FDA Facility Registration and Inspections",
                "Compliance with FDA Regulations",
                "GMP, HACCP, and Documentation Support",
                "Training and Internal Audits",
                "Certification body coordination",
              ].map((service, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{service}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 mb-6">
              We help businesses achieve FDA compliance, ensuring safe and
              effective products for the U.S. market and beyond.
            </p>
            <div className="border-t pt-6 mt-6">
              <p className="text-gray-800 font-semibold mb-4">Get in Touch:</p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-red-600" />
                  <span className="text-gray-700">
                    <strong>Email:</strong> atif@mbasolutions.co
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-red-600" />
                  <span className="text-gray-700">
                    <strong>Website:</strong> www.mbasolutions.co
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      <Footer />
    </>
  );
};

export default USFDA_Details;
